import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Building2, Search as SearchIcon, X } from 'lucide-react';
import { useAccount } from '../contexts/AccountContext';
import marketplaceService from '../services/marketplaceService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import CredentialOverrides from './shared/CredentialOverrides';
import CredentialSelector from './shared/CredentialSelector';
import CopyButton from './shared/CopyButton';
import HotelChooser from './shared/HotelChooser';
import UapiSurface from './shared/UapiSurface';
import UapiConsole from './shared/UapiConsole';
import UapiAuthenticationPage from './UapiAuthenticationPage';
import UapiLogsTab from './UapiLogsTab';
import UapiAccessProfilesTab from './UapiAccessProfilesTab';
import UapiRedactionsTab from './UapiRedactionsTab';
import UapiCustomCodeTab from './UapiCustomCodeTab';
import { formatDateTime } from '../utils/datetime';
import Button from './shared/Button';
import './OhipTransactionsPage.css';
import './shared/Tabs.css';
import './UniversalApiPage.css';

// Universal API — READ ONLY. What the Universal API exposes, and the credentials to call it.
//
// Moved out of Legacy (2026-09-07): this IS the Universal API offering now, not a pre-hub
// implementation being kept alive, so it sits at /universal-api level with the other products.
// The old /universal-api paths redirect here — they have been handed out.
//
// ROUTES form a tree (/hsk -> /hsk/v1) and OPERATIONS are the method + path pairs hanging off one.
//
// NOTHING IS EDITED HERE ANY MORE. The surface is derived from Oracle's published specs — imported and
// mirrored on Admin > Universal API > Payloads, where an operation arrives together with the payload
// that describes it. Hand-typed routes were the other way in, and they produced entries the specs do not
// have: an operation with no payload cannot be mapped, cannot be redacted and cannot be dispatched, so
// it reads as part of the API while serving nothing. This screen shows what is there.
//
// The tree is built from parent_id, NOT by splitting path strings. The sandbox originally inferred it
// from text and had to add parent_id later (its V24): with inference, renaming a segment silently
// orphans every descendant, because their stored paths stop matching the parent they are drawn under.

// Credential levels, in the order the credential picker shows its tabs. `account` is the CHAIN — the
// column is account_id and the UI has always called it Chain, so the label is translated here rather
// than leaving a user to work out that "account" means their hotel chain.
const CRED_LEVELS = [
    { key: 'partner', label: 'Partner' },
    { key: 'account', label: 'Chain' },
    { key: 'hotel', label: 'Hotel' },
];

// The two halves of the Universal API, as tabs on one screen.
//
// EACH TAB HAS ITS OWN URL. `/universal-api` is the surface, `/universal-api/authentication` the access
// list — the same paths as before they were merged, so every link already handed out still lands on the
// right tab, the detail screens' Back buttons still work, and the sidebar's nested entry still
// highlights. A tab held in state alone would have broken all three and made the tab unlinkable.

const TABS = [
    { key: 'surface', label: 'API Surface', path: '/universal-api' },
    { key: 'authentication', label: 'Authentication and Access', path: '/universal-api/authentication' },
    // NEXT TO AUTHENTICATION, because that is where it is attached: a hotel's backend binding is opened
    // there, and this is where the code it runs is listed with every other backend running it.
    { key: 'customcode', label: 'Custom Code', path: '/universal-api/custom-code' },
    // ORDERED AFTER AUTHENTICATION, because that is the order the work happens in: a property gets a
    // credential, then what it may reach is granted on top of it.
    { key: 'access', label: 'Access Profiles', path: '/universal-api/access-profiles' },
    // AFTER ACCESS PROFILES, because that is the order the work happens in: a profile says which calls
    // are granted, a redaction says what each answer is stripped to before it is served.
    { key: 'redactions', label: 'Redactions', path: '/universal-api/redactions' },
    { key: 'logs', label: 'Logs', path: '/universal-api/logs' },
];

// Per-tab subtitle. A tab with no entry gets no paragraph at all, rather than an empty one that still
// takes its margin.
const SUBTITLES = {
    authentication: 'Credentials to access the Universal API and backend PMS systems.',
    access: 'Which properties may reach the API, the backend each routes to, and what is granted on it.',
    customcode: 'Per-client code run before a backend system is called and after it answers.',
    redactions: 'What each document is stripped down to before it leaves the gateway, by audience.',
    logs: 'Every Universal API request this property has made, newest first. Traces are kept for three days.',
};

function UniversalApiPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const activeTab = TABS.find(t => t.key !== 'surface' && location.pathname.startsWith(t.path))?.key
        || 'surface';

    // The embedded Authentication tab hands its own header buttons up so they sit on the tab row rather
    // than inside the panel — one row of page actions, wherever you are.
    const [tabActions, setTabActions] = useState(null);
    // The API surface is shown in the context of a hotel, chosen in the top nav bar. Nothing loads
    // without one: the routes describe what a property's integration can call, so a list shown with no
    // property selected invites edits whose scope the person making them cannot see.
    const { selectedHotel, selectedAccount, selectedPartner, selectAccount, selectHotel } = useAccount();

    // Credentials that can authenticate to this API. Read-only here: creating and editing them is the
    // credential screens' job, and a second place to author the same record is how the two drift.
    const [credentials, setCredentials] = useState([]);
    const [credentialId, setCredentialId] = useState('');
    const [credSearch, setCredSearch] = useState('');
    const [credOpen, setCredOpen] = useState(false);
    // Needed by CredentialOverrides to load the setting definitions and find the must_be_overridden ones.
    // Per-instance override values, keyed by marketplace setting name (HOTELID today).
    // The call being tested, clicked in the route tree below. Held here rather than in either component
    // because the tree raises it and the console consumes it.
    // The call being tested. Null until a Test button is pressed; the modal is the only consumer.
    const [testOperation, setTestOperation] = useState(null);
    // Set when Test was pressed with no credential chosen. Cleared as soon as one is.

    // THE DIALOG ASKS FOR THE CREDENTIAL ITSELF, so Test opens whatever is chosen out here.
    //
    // It used to refuse to open without one and send the reader back up the page to a field that was
    // usually scrolled off the top. That put the question in the wrong place: the console is where a
    // call is composed, the credential is one of the things a call is composed OF, and being bounced
    // away to answer it is the interruption — not the answer being unknown.
    //
    // The console carries a credential picker of its own now, seeded from this page's choice when there
    // is one. So there is nothing to check here.
    const openTest = (op) => setTestOperation(op);
    // Target credentials: every credential this hotel can reach on a marketplace flagged
    // uapi_enabled (V417). This is the OTHER end of the call — the caller authenticates with the
    // Universal API credential above, and the uAPI then talks to one of these systems on its behalf.
    // The Universal-API-enabled systems, with names (marketplace.uapi_enabled, V417).
    // The chosen target: which system, and which of its credentials. CredentialSelector owns the
    // picker, so this page holds only the answer.
    // Per-hotel values for the target credential's must_be_overridden settings — HOTELID on OHIP
    // (V197). A chain or partner credential is shared across properties, so the property code cannot
    // come from the credential itself; without this a partner-level target is selected and there is
    // nowhere to say WHICH hotel it means.

    // Result of the last Authenticate press: { success, message } | null.
    const [authResult, setAuthResult] = useState(null);
    // Why the credential list is empty, when it is empty for a reason other than "there are none".
    const [credError, setCredError] = useState(null);
    const [authRunning, setAuthRunning] = useState(false);
    // The cached token for the selected credential: { credentialId, token, tokenType, expiresAt }.
    // Kept in component state only — never localStorage. A bearer token written to disk outlives the tab
    // it was fetched in and is readable by anything else served from this origin.
    const [token, setToken] = useState(null);
    const [tokenVisible, setTokenVisible] = useState(false);

    const tokenValid = token
        && String(token.credentialId) === String(credentialId)
        && token.expiresAt > Date.now();

    // Prove the selected credential can actually obtain a token, rather than only that it exists. The
    // exchange happens in the lambda — the browser sends a credential id and never sees the secret.
    //
    // Cached until it expires: a token is valid for its whole lifetime, and re-minting one on every
    // press both wastes the round trip and makes it impossible to keep working with the token you just
    // copied. A 30-second margin is taken off the expiry so a token that is about to lapse is replaced
    // rather than handed over.
    //
    // RETURNS THE TOKEN. State arrives on the next render, which is too late for a caller that wants to
    // send a request the moment it has one — the test console authenticates on your behalf and then
    // makes the call. Handing the token back lets it do both in one go, while the page's own state
    // still updates for the Authentication panel.
    // WHICH CREDENTIAL, ASKED FOR EXPLICITLY. The console can now choose one that is not this page's, so
    // minting always for `credentialId` would hand it a token issued to somebody else — a request that
    // authenticates as the wrong caller and is refused, or worse, is not.
    const authenticate = async (forCredentialId = credentialId) => {
        const mintFor = forCredentialId || credentialId;
        if (!mintFor) return null;
        // The cached token counts only when it belongs to the credential being asked about.
        if (tokenValid && String(token.credentialId) === String(mintFor)) {
            setAuthResult({ success: true, message: 'Using the cached token (still valid)' });
            return token;
        }
        setAuthRunning(true);
        setAuthResult(null);
        try {
            const res = await entityMktSettingsService.testSavedCredential(mintFor);
            const d = res.data || res;
            const at = d.data?.accessToken;
            let minted = null;
            if (d.success === true && at) {
                const ttl = Number(d.data.expiresIn) || 3600;
                minted = {
                    credentialId: mintFor,
                    token: at,
                    tokenType: d.data.tokenType || 'Bearer',
                    expiresAt: Date.now() + Math.max(0, ttl - 30) * 1000,
                    // The login exchange, shown by the console as step one of a call.
                    tokenUrl: d.data.tokenUrl || null, status: d.data.status || null, ms: d.data.ms ?? null, expiresIn: ttl,
                };
                setToken(minted);
                setTokenVisible(false);
            } else {
                setToken(null);
            }
            setAuthResult({ success: d.success === true, message: d.message || (d.success ? 'Authenticated' : 'Failed') });
            return minted;
        } catch (e) {
            setToken(null);
            setAuthResult({ success: false, message: e.response?.data?.message || e.message || 'Authentication failed' });
            return null;
        } finally {
            setAuthRunning(false);
        }
    };

    // The Universal API's own address, from the build-time env (REACT_APP_UAPI_URL, written by
    // envs/<env>/main.tf). Read-only: there is one gateway per environment at a domain we own, so it is
    // shown to be copied, never chosen.
    const gatewayUrl = process.env.REACT_APP_UAPI_URL || '';


    // The HCONNECT Universal API credentials visible to this hotel — its own, plus any inherited from
    // its chain or partner, which is what getPicker resolves.
    //
    // The marketplace is resolved BY NAME. V413 creates the row with an AUTO_INCREMENT id, so it differs
    // between dev and prd and must never be hardcoded.
    useEffect(() => {
        let cancelled = false;
        if (!selectedHotel) { setCredentials([]); return undefined; }
        (async () => {
            try {
                const cat = await marketplaceService.getCatalog();
                const list = cat.marketplaces || cat.settings || [];
                const mkt = list.find((m) => m.SYSTEMNAME === 'HCONNECT Universal API');
                if (!mkt || cancelled) return;
                const picker = await entityMktSettingsService.getPicker({
                    marketplaceId: mkt.MARKETPLACEID,
                    hotelId: selectedHotel,
                    accountId: selectedAccount,
                    partnerId: selectedPartner,
                });
                if (!cancelled) setCredentials(picker.credentials || []);
            } catch (e) {
                // A credential list that fails to load must not take the routes down with it — the
                // page's primary job is the API surface. But it is SHOWN as well as logged: swallowing
                // it silently left "No Universal API credentials for this hotel", which reads as a
                // provisioning problem at the property when the real cause was a broken page. That is
                // exactly what happened when this effect still called setters for state that had been
                // removed — the ReferenceError landed here and the screen blamed the data.
                console.error('[legacy-uapi] credential list failed:', e.message);
                if (!cancelled) setCredError(e.response?.data?.message || e.message || 'Could not load credentials');
            }
        })();
        return () => { cancelled = true; };
    }, [selectedHotel, selectedAccount, selectedPartner]);



    // Filtered on name and level, so "hotel" or a partner's name narrows the list as readily as the
    // credential's own name.
    const selectedCredential = credentials.find((c) => String(c.credential_id) === String(credentialId)) || null;
    const credMatches = credentials.filter((c) => {
        const q = credSearch.trim().toLowerCase();
        if (!q) return true;
        return String(c.credential_name || '').toLowerCase().includes(q)
            || String(c.entity_type || '').toLowerCase().includes(q);
    });

    // One decrypted setting off a credential returned by the picker.
    const settingValue = (cred, name) =>
        (cred?.settings || []).find((s) => s.setting_name === name)?.setting_value || '';

    return (
        <div className="otx-page">
            <div className="otx-header">
                <div>
                    <h1>Universal API</h1>
                    {/* ALWAYS RENDERED, even when empty. The API Surface tab has no subtitle — the
                        screen explains itself — but omitting the element made the header shrink on that
                        tab and grow on the other two, so the tab strip and everything under it jumped up
                        and down as you switched. The paragraph keeps a fixed two-line box (see
                        .uapi-subtitle) and the content below never moves. */}
                    <p className="otx-subtitle uapi-subtitle">{SUBTITLES[activeTab] || ''}</p>
                </div>
                {activeTab === 'authentication' && tabActions && (
                    <div className="otx-header-actions">{tabActions}</div>
                )}
            </div>

            {/* NO TABS UNTIL A HOTEL IS CHOSEN. All three of them answer in the context of one
                property — the routes it can reach, the credentials issued to it, the calls it made — so
                offering them before a property exists puts the reader one click from a screen that can
                only say the same thing this one is already saying. The chooser below is then the whole
                page, which is the single next step rather than one of four things to try. */}
            {selectedHotel && (
                <div className="hc-tabs">
                    {TABS.map(({ key, label, path }) => (
                        <button
                            key={key}
                            type="button"
                            className={`hc-tab${activeTab === key ? ' hc-tab-active' : ''}`}
                            onClick={() => navigate(path)}
                        >
                            <span className="hc-tab-label">{label}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Shown whichever tab the url names. A deep link to /universal-api/logs with no property
                selected used to render an empty Logs tab; it now asks the same question the surface tab
                asks, and the tab it was pointing at comes back the moment one is picked. */}
            {!selectedHotel && (
                <HotelChooser
                    selectedAccount={selectedAccount}
                    // The chain is already chosen — the list cannot be reached otherwise — so this only
                    // sets the hotel. The guard stays for the case where a hotel's own chain differs from
                    // the one selected: selectAccount clears the hotel by design, so it must run first or
                    // the selection would be dropped the moment it is made.
                    onPick={(hotel) => {
                        const accountId = hotel.accountid ?? hotel.accountId;
                        if (accountId && accountId !== selectedAccount) selectAccount(accountId);
                        selectHotel(hotel.hotelid);
                    }}
                />
            )}

            {selectedHotel && activeTab === 'authentication' && (
                <UapiAuthenticationPage embedded onRegisterActions={setTabActions} />
            )}

            {/* A profile is not per property — it is a named grant reused across them — but it is
                reached through this page's tab bar, which only exists once a hotel is chosen. The
                gate is the shell's, not the list's. */}
            {selectedHotel && activeTab === 'access' && <UapiAccessProfilesTab />}

            {selectedHotel && activeTab === 'redactions' && <UapiRedactionsTab />}

            {selectedHotel && activeTab === 'customcode' && <UapiCustomCodeTab />}

            {selectedHotel && activeTab === 'logs' && <UapiLogsTab />}

            {/* Which credential a caller would authenticate with. Read-only: this names an existing
                credential, it does not create or edit one — that stays in the credential screens so
                there is a single place a credential is authored.

                Below the header rather than beside it: with the overrides panel attached this is a
                two-field form, and a form sharing a flex row with the page title got squeezed into a
                20rem column while the title kept the rest of the width. It also reads in the right
                order — choose the credential, then the routes it can reach. */}
            {activeTab === 'surface' && selectedHotel && (
                <div className="lu-cred-picker">
                    {/* Read-only. One gateway per environment, at a domain the platform owns — it is
                        shown so it can be copied into a client, never chosen. Comes from the build-time
                        REACT_APP_UAPI_URL rather than a credential field, which is why V415 removed
                        GATEWAYURL from the marketplace. */}
                    <label className="lu-cred-label">Gateway URL</label>
                    <div className="lu-cred-actions lu-gateway-row">
                        <input
                            className="form-input lu-gateway-input"
                            value={gatewayUrl || 'not configured for this environment'}
                            readOnly
                            // Not `disabled`: a disabled input cannot be focused, so its value cannot be
                            // selected with the keyboard or read by a screen reader on the way past.
                            aria-readonly="true"
                            onFocus={(e) => e.target.select()}
                        />
                        {gatewayUrl && <CopyButton value={gatewayUrl} label="Copy" className="copy-btn-inline" />}
                    </div>

                    <label className="lu-cred-label lu-cred-label-spaced">Authenticate with</label>
                    <div className="lu-cred-control">
                        <SearchIcon size={14} className="lu-cred-icon" />
                        <input
                            className="form-input lu-cred-input"
                            placeholder={credentials.length
                                ? 'Search credentials…'
                                : credError
                                    // Distinct wording on purpose: an empty list and a list that failed
                                    // to load look identical and mean opposite things — one is a
                                    // property to provision, the other is a page to fix.
                                    ? 'Could not load credentials'
                                    : 'No Universal API credentials for this hotel'}
                            value={credOpen ? credSearch : (selectedCredential?.credential_name || '')}
                            onChange={(e) => { setCredSearch(e.target.value); setCredOpen(true); }}
                            onFocus={() => { setCredOpen(true); setCredSearch(''); }}
                            // Blur is delayed so a click on an option lands before the list unmounts.
                            onBlur={() => setTimeout(() => setCredOpen(false), 150)}
                            disabled={!credentials.length}
                        />
                        {selectedCredential && !credOpen && (
                            <span className="lu-cred-level-chip">
                                {(CRED_LEVELS.find((l) => l.key === selectedCredential.entity_type) || {}).label
                                    || selectedCredential.entity_type}
                            </span>
                        )}
                        {selectedCredential && !credOpen && (
                            <button
                                type="button"
                                className="lu-cred-clear"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { setCredentialId(''); setCredSearch(''); setAuthResult(null); }}
                                title="Clear"
                            >
                                <X size={13} />
                            </button>
                        )}
                        {credOpen && (
                            <div className="lu-cred-list">
                                {credMatches.length === 0 ? (
                                    <div className="lu-cred-empty">No credentials match “{credSearch}”</div>
                                ) : CRED_LEVELS.map(({ key, label }) => {
                                    const group = credMatches.filter((c) => c.entity_type === key);
                                    if (!group.length) return null;
                                    return (
                                        <div key={key} className="lu-cred-group">
                                            {/* Grouped most-specific LAST, matching the credential picker's own
                                                tab order — the hotel's own credential is the usual answer, so it
                                                sits closest to the cursor rather than scrolled past. */}
                                            <div className="lu-cred-group-label">{label}</div>
                                            {group.map((c) => (
                                                <button
                                                    key={c.credential_id}
                                                    type="button"
                                                    className={`lu-cred-option ${String(c.credential_id) === String(credentialId) ? 'lu-cred-option-on' : ''}`}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => { setCredentialId(c.credential_id); setCredOpen(false); setCredSearch(''); setAuthResult(null); }}
                                                >
                                                    <span className="lu-cred-name">{c.credential_name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {credError && (
                        <p className="lu-cred-error">
                            <AlertCircle size={13} /> {credError}
                        </p>
                    )}

                    {/* Prove the credential works. "Exists in the list" and "can obtain a token" are
                        different facts, and only the second one means a caller can use it — a client
                        whose secret was rotated, or whose app client was deleted, still shows here. */}
                    {selectedCredential && (
                        <div className="lu-cred-actions">
                            <Button variant="secondary" onClick={authenticate} disabled={authRunning}>
                                {authRunning ? 'Authenticating…' : 'Authenticate'}
                            </Button>
                            {authResult && (
                                <span className={authResult.success ? 'lu-auth-ok' : 'lu-auth-bad'}>
                                    {authResult.message}
                                </span>
                            )}
                        </div>
                    )}

                    {/* The token, once there is one. Masked by default: it is a live bearer credential
                        for the next hour, and a screen left open on a shared desk should not be one.
                        Copy works whether or not it is revealed — copying is the normal use, reading it
                        is not. */}
                    {tokenValid && (
                        <div className="lu-token">
                            <label className="lu-cred-label">Access token</label>
                            <div className="lu-token-row">
                                <code className="lu-token-value">
                                    {tokenVisible ? token.token : `${token.token.slice(0, 12)}${'•'.repeat(24)}`}
                                </code>
                                <button
                                    type="button"
                                    className="lu-token-toggle"
                                    onClick={() => setTokenVisible((v) => !v)}
                                >
                                    {tokenVisible ? 'Hide' : 'Show'}
                                </button>
                                <CopyButton value={token.token} label="Copy" className="copy-btn-inline" />
                            </div>
                            <p className="lu-token-note">
                                {token.tokenType} token, expires {formatDateTime(token.expiresAt)}. Send it as
                                {' '}<code>Authorization: {token.tokenType} &lt;token&gt;</code> to {gatewayUrl || 'the gateway'}.
                            </p>
                        </div>
                    )}

                    {/* The two credential values that travel as REQUEST HEADERS rather than being used
                        to obtain the token — so they are shown here, beside the token, where a caller
                        assembling a request needs them. Read-only: they belong to the credential, and
                        editing one here would edit it for every hotel that shares it.

                        Already in hand — the picker returns each credential's decrypted settings — so
                        this costs no extra round trip. */}
        </div>
            )}

            {activeTab === 'surface' && selectedHotel && (
                /* THE SURFACE, READ ONLY — the same component Admin > Universal API edits, not a copy of
                   it. This screen had its own tree, its own add rows and its own delete buttons, which
                   is two implementations of one thing: the day one gained a column or a guard the other
                   did not, and only one of them was the API. */
                <UapiSurface
                    readOnly
                    onTestOperation={openTest}
                    // What each call will be redacted to, for THIS credential at THIS property. Both are
                    // needed: the grant can name a set outright, and everything below that resolves from
                    // the hotel's chain and partner.
                    credentialId={credentialId}
                    hotelId={selectedHotel}
                />
            )}

            {/* Over the operation it is testing, opened from that row's Test button. */}
            {activeTab === 'surface' && testOperation && (
                <UapiConsole
                    operation={testOperation}
                    token={tokenValid ? token : null}
                    gatewayUrl={gatewayUrl}
                    hotelId={selectedHotel}
                    // The console authenticates for you rather than telling you to go and do it. It
                    // needs the credential to know whether that is even possible, and the function to
                    // do it with.
                    credentialId={credentialId}
                    // The dialog asks "call as" itself now, so it needs the list — not just this page's
                    // answer. Test opens with nothing chosen and the question is answered in place.
                    credentials={credentials}
                    onAuthenticate={authenticate}
                    onClose={() => setTestOperation(null)}
                />
            )}

        </div>
    );
}

export default UniversalApiPage;
